import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import { bootstrapAgentVoiceIfNeeded } from '../../services/agentVoiceBootstrap';
import { peekPendingOutboundDial } from '../../services/agentPhone/agentPhoneBridge';
import { getAgentPhonePath } from '../../services/agentPhone/agentPhoneNavigation';
import { webRTCCallService } from '../../services/WebRTCCallService';

/**
 * Boots Twilio Voice only on the Agent Phone route/window.
 * Registering the device on CRM routes conflicts with Agent Phone and breaks outbound calls.
 */
export const VoiceCallBootstrap: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const bootstrapInFlight = useRef(false);
  const onAgentPhone = location.pathname === getAgentPhonePath();

  const runBootstrap = useCallback(
    async (reason: string) => {
      if (loading || !user?._id || bootstrapInFlight.current || !onAgentPhone) {
        return;
      }

      if (peekPendingOutboundDial()) {
        console.log('[VoiceCallBootstrap] Skipping — queued outbound call will init voice');
        return;
      }

      bootstrapInFlight.current = true;
      try {
        console.log(`[VoiceCallBootstrap] ${reason}`);
        const result = await bootstrapAgentVoiceIfNeeded(user._id, user.firstName || 'Agent');
        if (result.error && !result.skipped) {
          console.warn('[VoiceCallBootstrap] Voice not ready:', result.error);
        }
      } finally {
        bootstrapInFlight.current = false;
      }
    },
    [loading, onAgentPhone, user?._id, user?.firstName],
  );

  // Release Twilio device when leaving Agent Phone so CRM tabs do not hold the same identity.
  useEffect(() => {
    if (onAgentPhone) {
      return;
    }
    if (webRTCCallService.isDeviceReady()) {
      console.log('[VoiceCallBootstrap] Leaving Agent Phone — releasing voice device');
      webRTCCallService.destroy();
    }
  }, [onAgentPhone, location.pathname]);

  useEffect(() => {
    if (!onAgentPhone) {
      return;
    }
    void runBootstrap('agent phone route');
  }, [onAgentPhone, runBootstrap]);

  useEffect(() => {
    if (!onAgentPhone) {
      return;
    }
    const onOnline = () => {
      void runBootstrap('browser online');
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [onAgentPhone, runBootstrap]);

  useEffect(() => {
    if (!onAgentPhone) {
      return;
    }
    const onVisibility = () => {
      if (document.hidden || !user?._id) {
        return;
      }
      webRTCCallService.reclaimStaleCallStateIfNeeded();
      if (!webRTCCallService.isDeviceReady()) {
        void runBootstrap('tab visible — device not registered');
      } else {
        webRTCCallService.ensureSocketAndAgentIdentified(user._id, user.firstName || 'Agent');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [onAgentPhone, runBootstrap, user?._id, user?.firstName]);

  return null;
};
